// ═══════════════════════════════════════════════════════════════════════
// Dispositifs sociaux (web, étape 3) — accès aux API des campagnes de vente subventionnée (moteur GÉNÉRIQUE) :
// campagnes et cycle de vie, inscriptions (guichet), achats (fournisseurs, devis, commandes, acompte, factures),
// réceptions et stock, attribution, remises, écarts, état de campagne ; actes de gestion d'une créance (FINANCE).
// Routes exactes : backend/src/dispositifs/campagnes/README.md § 2 et finance/README.md § 5.
// AUCUNE valeur métier ici : libellé, unités, prix, aide, durées et dates viennent de la campagne et de son
// règlement (Centre de configuration), toujours lus au serveur. Les tables ci-dessous ne traduisent que des codes.
// ═══════════════════════════════════════════════════════════════════════
import { useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';

const CA = '/api/dispositifs/campagnes';
const FIN = '/api/dispositifs/finance';

/** Erreur d'un téléchargement (corps reçu en Blob) : relit le JSON du serveur. */
async function erreurTelechargement(e) {
  const r = e?.response;
  if (r && typeof Blob !== 'undefined' && r.data instanceof Blob) {
    try {
      const d = JSON.parse(await r.data.text());
      return { statut: r.status, code: d.code || null, message: d.message || 'Téléchargement impossible.', erreurs: Array.isArray(d.erreurs) ? d.erreurs : [] };
    } catch { /* corps non JSON */ }
  }
  if (!r) return { statut: 0, code: 'RESEAU', message: 'Le serveur ne répond pas. Vérifiez la connexion puis réessayez.', erreurs: [] };
  return { statut: r.status, code: null, message: 'Téléchargement impossible.', erreurs: [] };
}

function client(token) {
  const entetes = token ? { Authorization: `Bearer ${token}` } : {};
  const get = async (url, params) => (await axios.get(url, { params, headers: entetes })).data.data;
  const post = async (url, corps, cle) => (await axios.post(url, corps || {}, { headers: cle ? { ...entetes, 'Idempotency-Key': cle } : entetes })).data.data;
  const sansVide = (o) => Object.fromEntries(Object.entries(o).filter(([, v]) => v !== '' && v !== null && v !== undefined));
  return {
    // Campagnes et cycle de vie
    campagnes: (org, { page = 1, taille = 25, etat } = {}) => get(CA, sansVide({ organisation: org, page, taille, etat })),
    campagne: (id) => get(`${CA}/${id}`),
    creerCampagne: (corps) => post(CA, corps),
    modifierCampagne: (id, corps) => post(`${CA}/${id}/modifier`, corps),
    transitionCampagne: (id, vers, motif) => post(`${CA}/${id}/transitions`, motif ? { vers, motif } : { vers }),
    // Passage simulation → réel (décision du 13/10) : acte dédié, clé ds_campagne_passer_reel, deux personnes, motif.
    preparerPassageReel: (id, motif, cle) => post(`${CA}/${id}/passage-reel`, { motif }, cle),
    confirmerPassageReel: (id, motif, cle) => post(`${CA}/${id}/passage-reel/confirmer`, { motif }, cle),
    rejeterPassageReel: (id, motif, cle) => post(`${CA}/${id}/passage-reel/rejeter`, { motif }, cle),
    reglementCampagne: (id, date) => get(`${CA}/${id}/reglement`, sansVide({ date })),
    simulationBudgetaire: (id) => get(`${CA}/${id}/simulation-budgetaire`),
    // Inscriptions (guichet : jamais pour soi)
    inscriptions: (id, { page = 1, taille = 25, statut } = {}) => get(`${CA}/${id}/inscriptions`, sansVide({ page, taille, statut })),
    simulerInscription: (id, corps) => post(`${CA}/${id}/inscriptions/simulation`, corps),
    inscrire: (id, corps, cle) => post(`${CA}/${id}/inscriptions`, corps, cle),
    verifierInscription: (id, motif) => post(`${CA}/inscriptions/${id}/verifier`, motif ? { motif } : {}),
    desisterInscription: (id, motif) => post(`${CA}/inscriptions/${id}/desister`, { motif }),
    leverAttribution: (id, motif) => post(`${CA}/inscriptions/${id}/lever-attribution`, { motif }),
    // Achats
    fournisseurs: (org) => get(`${CA}/fournisseurs`, { organisation: org }),
    creerFournisseur: (corps) => post(`${CA}/fournisseurs`, corps),
    achats: (id) => get(`${CA}/${id}/achats`),
    ajouterDevis: (id, corps) => post(`${CA}/${id}/devis`, corps),
    preparerCommande: (id, corps) => post(`${CA}/${id}/commandes`, corps),
    soumettreCommande: (commandeId) => post(`${CA}/commandes/${commandeId}/soumettre`),
    deciderCommande: (commandeId, corps, cle) => post(`${CA}/commandes/${commandeId}/decision`, corps, cle),
    annulerCommande: (commandeId, motif) => post(`${CA}/commandes/${commandeId}/annuler`, { motif }),
    preparerAcompte: (commandeId, corps, cle) => post(`${CA}/commandes/${commandeId}/acompte`, corps, cle),
    enregistrerFacture: (commandeId, corps, cle) => post(`${CA}/commandes/${commandeId}/factures`, corps, cle),
    // Réceptions, stock, attribution
    etablirReception: (commandeId, corps) => post(`${CA}/commandes/${commandeId}/receptions`, corps),
    contresignerReception: (receptionId) => post(`${CA}/receptions/${receptionId}/contresigner`),
    annulerReception: (receptionId, motif) => post(`${CA}/receptions/${receptionId}/annuler`, { motif }),
    stock: (id) => get(`${CA}/${id}/stock`),
    /** Mouvements de stock détaillés d'un lot (DM-WEB-003 n° 7) : route lue si le serveur la propose. */
    mouvementsStock: (id, lot) => get(`${CA}/${id}/stock/mouvements`, sansVide({ lot })),
    attributions: (id) => get(`${CA}/${id}/attribution`),
    calculerAttribution: (id) => post(`${CA}/${id}/attribution/calculer`),
    arreterAttribution: (calculId, cle) => post(`${CA}/attributions/${calculId}/arreter`, {}, cle),
    // Remises, écarts, état
    remises: (id) => get(`${CA}/${id}/remises`),
    remettre: (id, corps, cle) => post(`${CA}/${id}/remises`, corps, cle),
    ecarts: (id) => get(`${CA}/${id}/ecarts`),
    declarerEcart: (id, corps) => post(`${CA}/${id}/ecarts`, corps),
    deciderEcart: (ecartId, corps, cle) => post(`${CA}/ecarts/${ecartId}/decision`, corps, cle),
    // État agrégé par défaut ; détail nominatif sur demande (ds_export_nominatif, consultation tracée).
    etat: (id, { nominatif = false } = {}) => get(`${CA}/${id}/etat`, nominatif ? { detail: 'nominatif' } : undefined),
    /** Export CSV nominatif de l'état (tracé dans l'audit par le serveur) : { blob, type }. */
    etatCsv: async (id) => {
      try {
        const r = await axios.get(`${CA}/${id}/etat.csv`, { headers: entetes, responseType: 'blob' });
        return { blob: r.data, type: r.headers['content-type'] || r.data.type };
      } catch (e) { throw Object.assign(new Error('telechargement'), { normalisee: await erreurTelechargement(e) }); }
    },
    // FINANCE : actes de gestion d'une créance (proposés puis validés par une autre personne)
    proposerActeCreance: (creanceId, corps, cle) => post(`${FIN}/creances/${creanceId}/actes`, corps, cle),
    deciderActeCreance: (decisionId, corps, cle) => post(`${FIN}/actes/${decisionId}/decider`, corps, cle),
  };
}

/** Client des campagnes pour le composant courant. */
export function useCampagnes() {
  const { token } = useAuth();
  return useMemo(() => client(token), [token]);
}

// ─── Unités (toujours celles de la campagne) ───────────────────────────────────────────────────
/** « 3 <unités au pluriel> », « 1 <unité> » ; quantité absente → tiret long. */
export function quantiteUnites(campagne, q) {
  if (q === null || q === undefined || q === '') return '—';
  const n = Number(q);
  const u = n > 1 ? campagne?.unite_pluriel : campagne?.unite_singulier;
  return u ? `${n} ${u}` : String(n);
}

// ─── Libellés (codes du serveur → vocabulaire du gestionnaire) ─────────────────────────────────
export const ETATS_CAMPAGNE = {
  BROUILLON: ['neutre', 'Brouillon'], SIMULEE: ['info', 'Simulée'], A_VALIDER: ['alerte', 'À valider'], PUBLIEE: ['info', 'Publiée'],
  OUVERTE: ['succes', 'Inscriptions ouvertes'], INSCRIPTIONS_CLOSES: ['alerte', 'Inscriptions closes'], EN_DISTRIBUTION: ['info', 'En distribution'],
  DISTRIBUEE: ['succes', 'Distribuée'], EN_REMBOURSEMENT: ['info', 'En remboursement'], SOLDEE: ['succes', 'Soldée'], ARCHIVEE: ['neutre', 'Archivée'],
  ANNULEE: ['neutre', 'Annulée'],
};
export const CYCLE_CAMPAGNE = ['BROUILLON', 'SIMULEE', 'A_VALIDER', 'PUBLIEE', 'OUVERTE', 'INSCRIPTIONS_CLOSES', 'EN_DISTRIBUTION', 'DISTRIBUEE', 'EN_REMBOURSEMENT', 'SOLDEE', 'ARCHIVEE'];
/** États où une annulation motivée reste possible (avant la distribution). */
export const ANNULABLE = ['BROUILLON', 'SIMULEE', 'A_VALIDER', 'PUBLIEE', 'OUVERTE', 'INSCRIPTIONS_CLOSES'];
export const ETATS_INSCRIPTION = {
  INSCRITE: ['info', 'Inscription enregistrée'], VERIFIEE: ['info', 'Inscription vérifiée'], ATTRIBUEE: ['succes', 'Attribuée'],
  PARTIELLEMENT_REMISE: ['alerte', 'Remise en partie'], REMISE: ['succes', 'Remise'], DESISTEE: ['neutre', 'Désistement enregistré'],
  ANNULEE: ['neutre', 'Annulée'], ATTRIBUTION_LEVEE: ['neutre', 'Attribution levée'],
};
export const CANAUX = { MOBILE: 'Application', WEB: 'Espace salarié (web)', GUICHET: 'Guichet' };
export const STATUTS_COMMANDE = {
  BROUILLON: ['neutre', 'Brouillon'], A_VALIDER: ['alerte', 'À valider'], VALIDEE: ['succes', 'Validée'], PARTIELLEMENT_RECUE: ['alerte', 'Reçue en partie'],
  RECUE: ['succes', 'Reçue'], SOLDEE: ['neutre', 'Soldée'], ANNULEE: ['neutre', 'Annulée'],
};
export const STATUTS_RECEPTION = { A_CONTRESIGNER: ['alerte', 'À contresigner'], SIGNEE: ['succes', 'Signée'], ANNULEE: ['neutre', 'Annulée'] };
export const STATUTS_CALCUL = { PROPOSEE: ['alerte', 'À arrêter'], ARRETEE: ['succes', 'Arrêtée'], ABANDONNEE: ['neutre', 'Abandonnée (recalculée)'], REMPLACEE: ['neutre', 'Remplacée'] };
export const REGLES_ATTRIBUTION = {
  ORDRE_INSCRIPTION: 'Ordre d\'inscription', UNE_UNITE_PUIS_RESTE: 'Une unité à chacun, puis le reste dans l\'ordre', TIRAGE: 'Tirage au sort (graine publiée, reproductible)',
};
export const ORIGINES_STOCK = { RECEPTIONS: 'Reçu net (procès-verbaux signés)', COMMANDES: 'Commandes validées', REGLEMENT: 'Quantité disponible du règlement' };
export const PLAFONNEMENTS = { AU_STOCK: 'Inscriptions limitées à la quantité disponible', LIBRE_PUIS_ATTRIBUTION: 'Inscriptions libres, puis attribution' };
export const MODES_QUOTITE = { AVERTISSEMENT: 'Avertissement si la part retenue sur le salaire serait dépassée', BLOQUANT: 'Refus si la part retenue sur le salaire serait dépassée' };
export const MODES_REMBOURSEMENT = { RETENUE: 'Retenue sur salaire', DIRECT: 'Versements directs' };
export const STATUTS_ECART = { DECLARE: ['alerte', 'À décider'], TRAITE: ['succes', 'Traité'], REJETE: ['neutre', 'Rejeté'] };
export const TYPES_ECART = {
  LIVRAISON_PARTIELLE: 'Livraison partielle', PRIX_DIFFERENT: 'Prix final différent', PERTE: 'Perte', VOL: 'Vol', INVENDU: 'Invendu',
  RETOUR: 'Retour d\'une unité remise', AUTRE: 'Autre écart',
};
/** Traitements admis par type (liste fermée du serveur, rappelée ici pour le choix ; le serveur reste juge). */
export const TRAITEMENTS_PAR_TYPE = {
  LIVRAISON_PARTIELLE: ['CONSTAT', 'AVOIR_A_ETABLIR'], PRIX_DIFFERENT: ['CONSTAT', 'AVOIR_A_ETABLIR'], PERTE: ['SORTIE_STOCK'], VOL: ['SORTIE_STOCK'],
  INVENDU: ['CONSTAT', 'REVENTE', 'RETOUR_FOURNISSEUR'], RETOUR: ['RETOUR_STOCK'], AUTRE: ['CONSTAT', 'AVOIR_A_ETABLIR'],
};
export const TRAITEMENTS = {
  CONSTAT: 'Constat seulement (aucun mouvement)', AVOIR_A_ETABLIR: 'Avoir à établir par la trésorerie (deux personnes)', SORTIE_STOCK: 'Sortie du stock (perte)',
  REVENTE: 'Revente (sortie du stock)', RETOUR_FOURNISSEUR: 'Retour au fournisseur (sortie du stock)', RETOUR_STOCK: 'Retour en stock de l\'unité remise',
};
/** Types d'écart qui exigent une quantité (contrôle du serveur, rappelé pour guider la saisie). */
export const ECARTS_AVEC_QUANTITE = ['PERTE', 'VOL', 'INVENDU', 'RETOUR'];
export const ACTES_CREANCE = {
  SUSPENSION: 'Suspension des échéances', REPRISE: 'Reprise des échéances', REPORT: 'Report d\'échéances', REECHELONNEMENT: 'Rééchelonnement',
};
export const NATURES_STOCK = {
  recu: 'Reçu', remis: 'Remis', retour_salarie: 'Retours de salariés', perdu: 'Pertes', retourne_fournisseur: 'Retours au fournisseur', revendu: 'Revendu', en_stock: 'En stock',
};
